import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import authenticationApi from 'services/api/authentication';
import PasskeyService from 'services/PasskeyService';
import passkeysUtils from 'utils/passkeys';
import authenticationUtils from 'utils/Authentication';
import FormValidator from 'utils/FormValidator';
import ErrorConstants from 'utils/ErrorConstants';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'constants/LocalStorage';
import anaConsts from 'analytics/constants';
import ProcessEvent from 'analytics/processEvent';
import analyticsUtils from 'analytics/utils';
import { Text, Box, Button } from 'components/ui';
import TextInput from 'components/Inputs/TextInput/TextInput';
import {
    colors, space, fontSizes, lineHeights, fontWeights
} from 'style/config';
import ResetPasswordEN from 'pages/Auth/ResetPassword/locales/ResetPassword_en_US';
import ResetPasswordFR from 'pages/Auth/ResetPassword/locales/ResetPassword_fr_CA';
import { SCREENS } from 'pages/Auth/ResetPassword/constants';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(ResetPasswordEN, ResetPasswordFR);
const { isAuthServiceEnabled, updateProfileStatus } = authenticationUtils;
const PASSWORD_ERROR = ErrorConstants.ERRORS[ErrorConstants.ERROR_CODES.PASSWORD].message();

/**
 * SET_NEW_PASSWORD — SIM-18 / ACS-4355 / SUB-TASK-4.
 *
 * Migration of `components/ResetPassword/ResetPassword.ctrlr.jsx` (class →
 * function). Preserves the ACS-3079 passkey-invalidation killswitch flow
 * verbatim: on reset success, when `Sephora.configurationSettings
 * .isPasskeyResetPasswordInvalidation.isEnabled` AND
 * `passkeysUtils.isPasskeyEnabled()` are BOTH true, best-effort call to
 * `PasskeyService.invalidateAllPasskeys({ firstName, lastName })` (try/catch
 * so a passkey failure never brick the password reset). Analytics events
 * `EVENT_145` always, `EVENT_289` only when invalidation runs.
 *
 * Token validation runs on mount; failure transitions to TOKEN_ERROR (P4-SP7)
 * with an async page-load analytics event carrying `PASSWORD_RESET_TOKEN`.
 * Success transitions to RESET_SUCCESS (NOT a `showInfoModal` dispatch —
 * AMD-001 §3.1 mandates inline).
 */
// Scenario 8 scopes TOKEN_ERROR strictly to expired/invalid-token responses
// from `authenticationApi.resetPassword`. Other failure codes (weak password,
// rate limit, server 5xx, etc.) MUST surface inline on the SET_NEW_PASSWORD
// screen — same behaviour as the SIM-1 ctrlr, which set `errorMessage` in
// local state on submit failure rather than routing to the token-error page.
const isTokenExpiredOrInvalidError = errorCode => {
    if (!errorCode || typeof errorCode !== 'string') {
        return false;
    }

    const normalized = errorCode.toUpperCase();

    return normalized.includes('TOKEN') && (normalized.includes('EXPIR') || normalized.includes('INVALID'));
};

const SetNewPassword = ({ onTransition, sharedState }) => {
    const newPasswordRef = useRef(null);
    const confirmPasswordRef = useRef(null);
    const [newPassword, setNewPassword] = useState('');
    const [submitErrorMessage, setSubmitErrorMessage] = useState('');

    const user = useSelector(state => state?.user) || {};
    // Prefer `sharedState.email` (set by earlier screens); fall back to Redux.
    const email = sharedState?.email || user.login || '';
    const securityToken = sharedState?.securityToken || null;

    // Token validation on mount. The root's `useTokenBootstrap` already
    // validated the token as part of the initial-screen decision — but the
    // RED tests mount SetNewPassword directly with a stubbed API so this
    // component validates on its own too, matching the ctrlr contract.
    useEffect(() => {
        if (!securityToken) {
            return undefined;
        }

        let cancelled = false;
        authenticationApi.validatePasswordToken(securityToken).catch(error => {
            if (cancelled) {
                return;
            }

            const errors = error?.errors || [];
            const errorMessage = errors[0]?.errorMessage;
            const errorCode = errors[0]?.errorCode;

            if (typeof window !== 'undefined' && window.Sephora?.logger?.error) {
                // SIM18-SEC-M3: log only the redacted errorCode — never the
                // raw error object. Backends occasionally echo submitted
                // credentials (the reset token) or the account email inside
                // `error.errors[].details` or the envelope; passing the whole
                // object to logger.error can forward that to the prod log sink.
                window.Sephora.logger.error('validatePasswordToken_Failed:', { errorCode });
            }

            ProcessEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    ...analyticsUtils.getLastAsyncPageLoadData(),
                    pageName: `${anaConsts.PAGE_TYPES.USER_PROFILE}:${anaConsts.PAGE_DETAIL.MY_ACCOUNT}:n/a:*${anaConsts.PAGE_DETAIL.PASSWORD_RESET_TOKEN_ERROR}`,
                    pageType: anaConsts.PAGE_TYPES.USER_PROFILE,
                    pageDetail: anaConsts.PAGE_DETAIL.MY_ACCOUNT,
                    prop28: anaConsts.FIELD_ERRORS.PASSWORD_RESET_TOKEN,
                    prop48: errorMessage
                }
            });
            onTransition(SCREENS.TOKEN_ERROR, { securityToken });
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [securityToken]);

    const validatePassword = input => {
        const { passwordMin, passwordMax } = FormValidator.FIELD_LENGTHS;

        if (FormValidator.isEmpty(input) || !FormValidator.isValidLength(input, passwordMin, passwordMax) || FormValidator.hasEmptySpaces(input)) {
            return PASSWORD_ERROR;
        }

        return null;
    };

    const validateConfirm = input => {
        if (FormValidator.isEmpty(input)) {
            return PASSWORD_ERROR;
        }

        if (input !== newPassword) {
            return getText('confirmError');
        }

        return null;
    };

    const isValid = () => {
        const newErr = newPasswordRef.current?.validateError();
        const confErr = confirmPasswordRef.current?.validateError();

        return !newErr && !confErr;
    };

    const submit = async e => {
        e.preventDefault();

        if (!isValid()) {
            return;
        }

        setSubmitErrorMessage('');
        const newPw = newPasswordRef.current.getValue();
        const confirmPw = confirmPasswordRef.current.getValue();
        const authEnabled = isAuthServiceEnabled();

        try {
            const response = await authenticationApi.resetPassword(email, newPw, confirmPw, securityToken);

            if (authEnabled) {
                Storage.local.setItem(LOCAL_STORAGE.PROFILE_ID, response?.profileId);

                if (response?.jwtResponse) {
                    updateProfileStatus({
                        profileSecurityStatus: [response.jwtResponse.profileSecurityStatus],
                        accessToken: [response.jwtResponse.accessToken, response.jwtResponse?.atExp],
                        refreshToken: [response.jwtResponse.refreshToken, response.jwtResponse?.rtExp]
                    });
                }
            }

            const isPasskeyInvalidationEnabled =
                passkeysUtils.isPasskeyEnabled() && window.Sephora?.configurationSettings?.isPasskeyResetPasswordInvalidation?.isEnabled;

            const analyticsData = {
                eventStrings: [anaConsts.Event.EVENT_145]
            };

            if (isPasskeyInvalidationEnabled) {
                try {
                    analyticsData.actionInfo = anaConsts.LinkData.PASSKEY_REMOVED_ON_RESET_PASSWORD;
                    analyticsData.eventStrings.push(anaConsts.Event.EVENT_289);
                    // Best-effort — a passkey failure never blocks the reset flow.
                    await PasskeyService.invalidateAllPasskeys({
                        firstName: user.firstName,
                        lastName: user.lastName
                    });
                } catch (invalidationError) {
                    window.Sephora?.logger?.warn?.('[Passkey] Passkey invalidation failed after password reset', {
                        error: invalidationError?.message || invalidationError
                    });
                }
            }

            // `ProcessEvent.process` fires-and-forgets a dynamic
            // `import('analytics/getBindingMethods')` internally (see
            // `analytics/processEvent.js`). Under jsdom that import can
            // synthesize an unhandled Promise rejection when analytics
            // bindings depend on runtime globals that are not populated —
            // node crashes the whole worker on unhandled rejection. Pre-warm
            // the import ourselves so we can attach a `.catch` and keep the
            // reset-success transition unblocked. Matches the pattern used
            // in `SignIn.ctrlr.jsx`'s `dispatchAnalyticsSafely` helper.
            import(/* webpackMode: "eager" */ 'analytics/getBindingMethods')
                .then(() => {
                    ProcessEvent.process(anaConsts.LINK_TRACKING_EVENT, { data: analyticsData });
                })
                .catch(analyticsError => {
                    window.Sephora?.logger?.warn?.('[Analytics] LINK_TRACKING_EVENT dispatch failed after password reset', {
                        error: analyticsError?.message || analyticsError
                    });
                });

            onTransition(SCREENS.RESET_SUCCESS, {
                shouldMakeUserFullCall: Boolean(authEnabled && response?.profileStatus !== 0)
            });
        } catch (error) {
            const errors = error?.errors || [];
            const errorCode = errors[0]?.errorCode;
            const errorMessage = errors[0]?.errorMessage;

            // Scenario 8 (per implementation plan): TOKEN_ERROR is reserved for
            // expired/invalid-token responses. All other failures (weak
            // password, rate limit, transient server error) stay on this
            // screen with an inline `role="alert"` message so the user can
            // retry without losing the token in the URL.
            if (isTokenExpiredOrInvalidError(errorCode)) {
                onTransition(SCREENS.TOKEN_ERROR, { securityToken, errorMessage });

                return;
            }

            setSubmitErrorMessage(errorMessage || getText('errorMessage'));
        }
    };

    return (
        <>
            <Text
                is='h1'
                fontSize={fontSizes.lg}
                fontWeight={fontWeights.bold}
                lineHeight={lineHeights.tight}
                marginBottom={4}
            >
                {getText('pageTitle')}
            </Text>

            <Text
                is='p'
                fontSize={fontSizes.md}
                lineHeight={lineHeights.base}
                marginBottom={5}
            >
                {getText('pleaseCreateNewPassword')}
            </Text>

            {submitErrorMessage ? (
                <Box
                    role='alert'
                    css={styles.errorAlert}
                    marginBottom={4}
                >
                    {submitErrorMessage}
                </Box>
            ) : null}

            <Box
                is='form'
                noValidate
                onSubmit={submit}
            >
                <Box marginBottom={3}>
                    <TextInput
                        name='newPassword'
                        type='password'
                        autoCorrect='off'
                        required={true}
                        label={getText('newPassword')}
                        ref={newPasswordRef}
                        onChange={e => setNewPassword(e.target.value)}
                        validate={validatePassword}
                        hideAsterisk={true}
                    />
                </Box>
                <Box marginBottom={4}>
                    <TextInput
                        name='confirmPassword'
                        type='password'
                        autoCorrect='off'
                        required={true}
                        label={getText('confirmPassword')}
                        ref={confirmPasswordRef}
                        validate={validateConfirm}
                        hideAsterisk={true}
                    />
                </Box>
                <Button
                    variant='primary'
                    type='submit'
                    width='100%'
                    paddingY={space[3]}
                >
                    {getText('resetPasswordButton')}
                </Button>
            </Box>

            <Box marginTop={5}>
                <Text
                    is='p'
                    color={colors.gray}
                    fontSize={fontSizes.xs}
                    textAlign='center'
                >
                    {getText('unableToResetPassword')}
                </Text>
            </Box>
        </>
    );
};

const styles = {
    // AC-023: colors.red on white is WCAG-AA (~5.3:1).
    errorAlert: {
        color: colors.red
    }
};

export default wrapFunctionalComponent(SetNewPassword, 'SetNewPassword');
